import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Tooltip } from '@swyftx/aviary/atoms/Tooltip/Tooltip';
import { Heading, Body, Utility } from '@swyftx/aviary/atoms/Typography';
import { Blockearner, ArrowLineOnlyDown, Information, Percent, Bitcoin } from '@swyftx/aviary/icons/outlined';
import { Clock24Hours } from '@swyftx/aviary/icons/outlined/Clock24Hours';
import { GridOverlay } from '@swyftx/aviary/molecules/FeaturePromoBanner/GridOverlay';

import { AccessCashCard } from './AccessCashCard';

type AccessCashBannerProps = {
  hasError: boolean;
};

const AccessCashBanner: React.FC<AccessCashBannerProps> = ({ hasError }) => {
  const { t } = useTranslation('loans');

  const rowItem = (
    body: string,
    icon: React.ReactNode,
    toolTipSide?: 'top' | 'bottom',
    toolTipHeader?: string,
    toolTipBody?: string,
  ): JSX.Element => (
    <div className='flex flex-row items-center space-x-[4px]'>
      {icon}
      <Body size='medium' color='inverse'>
        {body}
      </Body>
      {(toolTipHeader || toolTipBody) && (
        <Tooltip
          backgroundColor='bg-color-background-surface-primary'
          align='center'
          side={toolTipSide ? toolTipSide : 'top'}
          sideOffset={8}
          className='w-[196px]'
          content={
            <div className='space-y-[4px] rounded-4 bg-color-background-surface-primary'>
              <Body size='xsmall' color='primary' weight='bold'>
                {toolTipHeader}
              </Body>
              <Body size='xsmall' color='primary'>
                {toolTipBody}
              </Body>
            </div>
          }
        >
          <div className='flex items-center'>
            <Information className='h-[20px] w-[20px] min-w-[24px] cursor-pointer text-color-icon-inverse' />
          </div>
        </Tooltip>
      )}
    </div>
  );

  return (
    <Card className='relative overflow-hidden bg-color-background-secondary' border={false}>
      <GridOverlay className='pointer-events-none absolute inset-0 z-0 h-full w-full scale-[3.5] object-cover opacity-30 md:scale-[1.5]' />
      <div className='relative z-10 grid grid-cols-1 gap-y-4 bg-[linear-gradient(104deg,_transparent_51.24%,_var(--access-cash-gradient-overlay)_64%)] p-32 md:grid-cols-2 md:gap-x-[32px] lg:grid-cols-3 lg:gap-y-0'>
        <div className='order-1 space-y-[24px] p-4 md:order-none lg:col-span-2 lg:row-start-1'>
          {/* Content */}
          <FlexLayout className='flex-col space-y-[8px]'>
            <Heading size='h2' className='text-center md:text-start' weight='light' color='inverse'>
              {t('AccessCashBanner.information.header.title')}
            </Heading>
            <Body size='large' className='text-center md:text-start' color='inverse'>
              {t('AccessCashBanner.information.header.description')}{' '}
            </Body>
          </FlexLayout>

          <div className='hidden flex-col space-y-[8px] md:block'>
            {rowItem(
              t('AccessCashBanner.information.listItem.1'),
              <Bitcoin color='inverse' className='h-16 w-16 text-color-text-inverse' />,
            )}
            {rowItem(
              t('AccessCashBanner.information.listItem.2'),
              <Clock24Hours color='inverse' className='h-16 w-16 text-color-text-inverse' />,
            )}
            {rowItem(
              t('AccessCashBanner.information.listItem.3'),
              <ArrowLineOnlyDown color='inverse' className='h-16 w-16 text-color-text-inverse' />,
              'top',
              t('AccessCashBanner.information.tooltip.title1'),
              t('AccessCashBanner.information.tooltip.description1'),
            )}
            {rowItem(
              t('AccessCashBanner.information.listItem.4'),
              <Percent color='inverse' className='h-16 w-16 text-color-text-inverse ' />,
              'bottom',
              t('AccessCashBanner.information.tooltip.title2'),
              t('AccessCashBanner.information.tooltip.description2'),
            )}
          </div>

          <div className='flex w-full flex-col justify-center space-y-[8px] space-y-[8px] rounded-8 p-4 md:w-1/2 md:justify-start'>
            <Utility className='text-center md:text-start' variant='overline' color='inverse' weight='bold'>
              {t('AccessCashBanner.information.blockearnerLabel')}
            </Utility>
            <div className='flex justify-center md:justify-start'>
              <Blockearner className='text-color-text-inverse' />
            </div>
          </div>
        </div>

        <div className='align-self-center order-2 flex justify-center rounded py-24 md:col-start-2 md:row-span-2 md:row-start-1 md:py-0 lg:col-start-3 lg:row-span-2 lg:row-start-1'>
          <AccessCashCard hasError={hasError} />
        </div>

        <div className='order-3 p-4 md:order-none lg:col-span-2 lg:row-start-2'>
          <Utility className='text-center md:text-start' variant='caption' color='inverse'>
            {/** TODO: add real values for [INSERT CREDIT REPNUMBER], [INSERT ACN], [INSERT ACL] */}
            {t('AccessCashBanner.information.footer')}
          </Utility>
        </div>
      </div>
    </Card>
  );
};

AccessCashBanner.displayName = 'AccessCashBanner';

export { AccessCashBanner };
